import React, { useEffect, useRef, useState } from 'react';
import { LeftIconWrapper, Option, OptionWrapper, OptionsMenuInnerWrapper, OptionsMenuWrapper } from './subcomponents';
import { DOWN_ARROW_KEY, ENTER_KEY, UP_ARROW_KEY } from '../../../const/keyNames';
const OptionsMenu = ({ menuPosition, options, selectedOption, tabIndex, setSelectedOption, setIsMenuOpen, id, isMenuOpen }) => {
    const optionsRefs = useRef([]);
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(0); // By default it's 0 because we focus on the first option
    const isOptionSelected = (option) => {
        if (selectedOption) {
            return (option.label === selectedOption.label &&
                option.value === selectedOption.value);
        }
        return false;
    };
    const setFocusedOption = () => {
        var _a;
        if (isMenuOpen && ((_a = optionsRefs === null || optionsRefs === void 0 ? void 0 : optionsRefs.current) === null || _a === void 0 ? void 0 : _a.length)) {
            optionsRefs.current[focusedOptionIndex].focus();
        }
    };
    const handlePressArrowUp = () => {
        /**
         * When we press arrow UP, we need to move the focus to the option ABOVE the current one, so we want the focusedOptionIndex - 1
         * and we need to make sure we don't get a number smaller than 0 (because that wouldn't be a valid index in the array of options)
         */
        const newFocusedOptionIndex = focusedOptionIndex > 1 ? focusedOptionIndex - 1 : 0;
        setFocusedOptionIndex(newFocusedOptionIndex);
    };
    const handlePressArrowDown = () => {
        /**
         * When we press arrow DOWN, we need to move the focus to the option BELOW the current one, so we want the focusedOptionIndex + 1
         * and we need to make sure we don't get a number greater than the last option index (because that wouldn't be a valid index in the array of options)
         */
        const lastOptionIndex = options.length - 1;
        const newFocusedOptionIndex = focusedOptionIndex + 1 <= lastOptionIndex
            ? focusedOptionIndex + 1
            : lastOptionIndex;
        setFocusedOptionIndex(newFocusedOptionIndex);
    };
    const handleKeyDown = (event) => {
        if (event.key === UP_ARROW_KEY) {
            handlePressArrowUp();
        }
        if (event.key === DOWN_ARROW_KEY) {
            handlePressArrowDown();
        }
    };
    const handleOptionElementClick = (option) => {
        setIsMenuOpen(false);
        if (option.onClick) {
            option.onClick();
        }
    };
    const handleFocusOnSelect = () => {
        var _a;
        return (_a = document.getElementById(`${id}-combobox`)) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const addOptionRef = (option, index) => {
        if (option) {
            optionsRefs.current[index] = option;
        }
    };
    useEffect(() => {
        if (selectedOption) {
            /**
             * Here we get the index of the currently selected option, so we can focus it using its ref.
             * If the user chose something and then closed the panel, we need this index to know what option we should focus next when we press arrow up/down.
             */
            const selectedOptionIndex = options.findIndex((option) => (option === null || option === void 0 ? void 0 : option.value) === selectedOption.value);
            // If selectedOptionIndex is > 0, we focus that option, otherwise we focus the first option (with index 0)
            setFocusedOptionIndex(Math.max(selectedOptionIndex, 0));
            return;
        }
        /*
         * If there's no selected option, we focus on the first option by default.
         */
        setFocusedOptionIndex(0);
    }, [isMenuOpen, selectedOption, options]);
    useEffect(setFocusedOption, [setFocusedOption, isMenuOpen, optionsRefs]);
    return (React.createElement(OptionsMenuWrapper, { placement: menuPosition },
        React.createElement(OptionsMenuInnerWrapper, { id: `${id}-options-inner-wrapper`, role: "listbox" }, options.map((option, index) => {
            if (option.value) {
                return (React.createElement(OptionWrapper, { hasIcon: !!option.icon, tabIndex: tabIndex, "aria-label": option.label, "data-type": option.dataType, "data-object": option.dataObject, "data-reason": option.dataReason, selected: isOptionSelected(option), "aria-selected": isOptionSelected(option), id: `${id}-option-${index}`, key: `${id}-${option.label}`, className: "Select__option--open", role: "option", ref: (el) => addOptionRef(el, index), onClick: () => {
                        setSelectedOption(option);
                        setIsMenuOpen(false);
                        handleFocusOnSelect();
                    }, onKeyPress: (event) => {
                        if (event.key === ENTER_KEY) {
                            setSelectedOption(option);
                            setIsMenuOpen(false);
                            handleFocusOnSelect();
                        }
                    }, onKeyDown: (event) => handleKeyDown(event) }, option.element ? (option.element) : (React.createElement(React.Fragment, null,
                    option.icon && (React.createElement(LeftIconWrapper, null, option.icon)),
                    React.createElement(Option, null, option.label)))));
            }
            return (React.createElement(OptionWrapper, { key: `${index + 1}`, ref: (el) => addOptionRef(el, index), role: "option", "aria-label": option.label, "aria-selected": isOptionSelected(option), tabIndex: tabIndex, "data-type": option.dataType, "data-object": option.dataObject, "data-reason": option.dataReason, onKeyDown: (event) => handleKeyDown(event), onClick: () => handleOptionElementClick(option), onKeyPress: (event) => {
                    if (event.key === ENTER_KEY) {
                        handleOptionElementClick(option);
                    }
                } }, option.element));
        }))));
};
export default OptionsMenu;
